/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import {
	dataTypes
} from './Types';

export default class TargetEncoder {
	/**
	 * @param {Array.<Array.<number|boolean|string>>} x: list of lists to be inputed to the model. Each nested list represents a row of data.
	 * @param {Array.<dataTypes>} types: List containing data types of each column (only categorical columns are encoded).
	 * @param {Array.<number>} y: List of target column data. Each entry is a row
	 * @example:
	 * +-----+--------+--------+
		| Age | Gender | Income |
		+-----+--------+--------+
		| 33  | M      | 55     |
		+-----+--------+--------+
		| 45  | M      | 88     |
		+-----+--------+--------+
		| 23  | F      | 64     |
		+-----+--------+--------+
	 * if x is Age and Gender, and y is Income,
	 * x = [ [33,M],[45,M],[23,F] ], y = [55,88,64], and types = [measure, categorical]
	 * @constructor
	 */
	constructor(x, types, y) {
		if (x === undefined || types === undefined || y === undefined) {
			this.averages = null;
			this.yMean = null;
			this.types = null;
		} else {
			this.yMean = y.reduce((a, b) => a + b, 0) / y.length
			this.types = types;
			this.averages = this.computeAverges(x, types, y);
		}
	}

	setTypes(types) {
		this.types = types;
	}

	setYMean(yMean) {
		this.yMean = yMean;
	}

	setAverages(averages) {
		this.averages = averages;
	}

	computeAverges(x, types, y) {
		let averages = types.map(type => {
			if (type === dataTypes.CATEGORICAL) {
				return {}
			}
			return null
		});
		// each categorical col is represented by a dict containing unique value counts and their y sum 
		x.map((row, rowIndex) => {
			this.checkInput(row)
			row.map((value, colIndex) => {
				if (types[colIndex] === dataTypes.CATEGORICAL) {
					if (value in averages[colIndex]) {
						averages[colIndex][value].count += 1
						averages[colIndex][value].sum += y[rowIndex]
					} else {
						averages[colIndex][value] = {
							count: 1,
							sum: y[rowIndex]
						}
					}
				}
			});
		});
		// compute averges
		let res = types.map(type => {
			if (type === dataTypes.CATEGORICAL) {
				return {}
			}
			return null
		});

		averages.map((col, colIndex) => {
			if (types[colIndex] === dataTypes.CATEGORICAL) {
				Object.keys(col).map((value) => {
					res[colIndex][value] = averages[colIndex][value].sum / averages[colIndex][value].count;
				});

			}
		});
		return res;
	}

	// confirm input row size is correct
	checkInput(row) {
		if (row.length !== this.types.length) {
			throw new Error('Input size error')
		}
	}

	/**
	 * Encodes & concatenates rows when predicting values
	 *
	 * @param {Array.<Array.<number|boolean|string>>} x: list of rows to encode
	 * @returns {Array.<Array.<number>>} list of encoded rows
	 */
	encode(x) {
		return x.map((row) => {
			this.checkInput(row)

			return row.map((value, colIndex) => {
				if (this.types[colIndex] === dataTypes.CATEGORICAL) {
					if (value in this.averages[colIndex]) {
						// if value seen before, return its y average
						return this.averages[colIndex][value];
					}
					// if new value return overall y average
					return this.yMean
				}
				// if not categorical return orginal value
				return value
			})
		})
	}
}